/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ==========================================================================
   ENHANCED VARIANT DROPDOWN & INITIAL UNSELECTED STATE LOGIC (OPTION B)
   ========================================================================== */
(function() {
  // Flag to know if user has actively made variant selections on this page session
  window.__userInteractedWithVariants = false;

  // Clean ?variant= parameter from URL on initial landing to prevent sticky pre-selections
  if (!window.__userInteractedWithVariants) {
    try {
      if (window.location.search && window.location.search.includes('variant=')) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('variant');
        window.history.replaceState({ path: cleanUrl.toString() }, '', cleanUrl.toString());
      }
    } catch (e) {
      // Ignore URL parsing errors
    }
  }

  function initEnhancedVariantSelectors() {
    const variantPickers = document.querySelectorAll('variant-picker');
    if (!variantPickers.length) return;

    variantPickers.forEach(picker => {
      const formId = picker.getAttribute('form-id');
      const form = document.forms[formId] || document.getElementById(formId);
      if (!form) return;

      const variantsDataEl = picker.querySelector('script[id^="product-variants-data-"]');
      let variants = [];
      if (variantsDataEl) {
        try {
          variants = JSON.parse(variantsDataEl.textContent || '[]');
        } catch (e) {
          console.error('Error parsing product variants:', e);
        }
      }

      const atcButton = form.querySelector('[data-product-add-to-cart-button]') || form.querySelector('#AddToCart');
      const paymentButtonWrapper = form.querySelector('.shopify-payment-button');
      const promptEl = picker.closest('.product__info')?.querySelector('.variant-selection-prompt') || document.querySelector('.variant-selection-prompt');
      const stickyBtn = document.getElementById('clStickyBtn') || document.getElementById('StickyAddToCart');

      // Helper: reset all dropdowns and inputs to clean unselected state
      function resetPickerToUnselected() {
        if (window.__userInteractedWithVariants) return;

        // Reset hidden option inputs (used in dropdowns only)
        picker.querySelectorAll('input[type="hidden"][data-option-position]').forEach(input => {
          input.value = '';
          input.removeAttribute('data-selected-text');
        });

        // Reset radio option inputs (swatches / amazon cards) without wiping their value attributes!
        picker.querySelectorAll('input[type="radio"][data-option-position]').forEach(radio => {
          radio.checked = false;
        });

        // Reset card selected classes
        picker.querySelectorAll('.amazon-variant-card.is-selected').forEach(card => {
          card.classList.remove('is-selected');
        });

        // Reset block swatch selected classes
        picker.querySelectorAll('.block-swatch__item.is-selected').forEach(item => {
          item.classList.remove('is-selected');
        });

        // Reset option value displays above selectors to "Select [Option]"
        picker.querySelectorAll('.product-form__option-selector').forEach(sel => {
          const type = sel.getAttribute('data-selector-type');
          if (type !== 'dropdown') {
            const valSpan = sel.querySelector('.product-form__option-value');
            if (valSpan && !valSpan.querySelector('.is-placeholder')) {
              const optName = sel.querySelector('.product-form__option-name')?.textContent?.replace(':', '').trim() || 'option';
              valSpan.innerHTML = `<span class="is-placeholder text--subdued">Select ${optName}</span>`;
            }
          }
        });

        // Reset dropdown buttons
        picker.querySelectorAll('.select--visible-enhanced').forEach(btn => {
          btn.classList.remove('has-selection');
          const valueSpan = btn.querySelector('.select__selected-value');
          if (valueSpan) {
            valueSpan.classList.add('is-placeholder');
            const targetId = btn.getAttribute('aria-controls');
            const targetBox = targetId ? document.getElementById(targetId) : null;
            const optName = targetBox?.querySelector('.combo-box__title')?.textContent?.trim() || 'option';
            valueSpan.textContent = 'Select ' + optName;
          }
        });

        // Reset aria-selected on combo-box options
        picker.querySelectorAll('.combo-box__option-item').forEach(item => {
          item.setAttribute('aria-selected', 'false');
        });

        // Reset main form ID input
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }

        // Reset sticky form dropdowns and selects if present
        document.querySelectorAll('.product-sticky-form select[data-option-position]').forEach(select => {
          select.value = '';
        });
        document.querySelectorAll('.product-sticky-form .select__selected-value').forEach(span => {
          span.classList.add('is-placeholder');
          const parentBox = span.closest('.product-sticky-form__option-selector')?.querySelector('.combo-box__title')?.textContent?.trim() || 'option';
          span.textContent = 'Select ' + parentBox;
        });
      }

      // Helper to inspect selections across ALL selector types (dropdowns, cards, radios, links)
      function getActiveOptionSelections() {
        const optionSelectors = Array.from(picker.querySelectorAll('.product-form__option-selector'));
        const selections = {};

        optionSelectors.forEach((selector, index) => {
          const pos = index + 1;
          const optName = selector.querySelector('.product-form__option-name')?.textContent?.replace(':', '').trim() || ('Option ' + pos);

          let selectedVal = '';
          let selectedText = '';

          // Dropdown hidden input
          const hiddenInput = selector.querySelector('input[type="hidden"][data-option-position]');
          if (hiddenInput && hiddenInput.value) {
            selectedVal = hiddenInput.value.trim();
            selectedText = hiddenInput.getAttribute('data-selected-text') || selectedVal;
          }

          // Radio input (cards, swatches, blocks)
          const checkedRadio = selector.querySelector('input[type="radio"][data-option-position]:checked');
          if (checkedRadio) {
            selectedVal = checkedRadio.value.trim();
            selectedText = checkedRadio.getAttribute('data-option-text') || checkedRadio.getAttribute('aria-label') || checkedRadio.closest('.amazon-variant-card')?.querySelector('.amazon-variant-card__title')?.textContent?.trim() || selectedVal;
          }

          // Combined listing link
          const selectedLink = selector.querySelector('.amazon-variant-card__link.is-selected, .block-swatch__item.is-selected');
          if (selectedLink) {
            selectedText = selectedLink.getAttribute('data-option-text') || selectedLink.getAttribute('title') || selectedLink.querySelector('.amazon-variant-card__title')?.textContent?.trim() || selectedLink.textContent?.trim() || '';
            selectedVal = selectedText;
          }

          selections[pos] = {
            position: pos,
            name: optName,
            value: selectedVal,
            text: selectedText,
            isSelected: !!(selectedVal && selectedText)
          };
        });

        return selections;
      }

      // Dynamic availability calculation across multi-level options
      function updateOptionsAvailability() {
        if (!variants || !variants.length) return;

        const activeSelections = getActiveOptionSelections();
        const currentSelections = {};
        for (const [pos, sel] of Object.entries(activeSelections)) {
          if (sel.isSelected) {
            currentSelections[pos] = sel.text;
          }
        }

        const optionSelectors = Array.from(picker.querySelectorAll('.product-form__option-selector'));

        optionSelectors.forEach((selector, index) => {
          const pos = index + 1;
          const optIndex = pos - 1;
          const comboBox = selector.querySelector('combo-box');

          // Position 1 should NEVER be disabled by downstream selections
          if (pos === 1) {
            if (comboBox) {
              const optionItems = comboBox.querySelectorAll('.combo-box__option-item');
              optionItems.forEach(item => {
                const itemText = item.getAttribute('data-option-text') || item.textContent.trim();
                const hasAnyAvailable = variants.some(v => {
                  const vVal = v.options[0] || v.option1;
                  return (vVal === itemText) && v.available === true;
                });
                if (hasAnyAvailable) {
                  item.classList.remove('is-disabled');
                } else {
                  item.classList.add('is-disabled');
                }
              });
            }
            return;
          }

          if (comboBox) {
            const optionItems = comboBox.querySelectorAll('.combo-box__option-item');
            optionItems.forEach(item => {
              const itemText = item.getAttribute('data-option-text') || item.textContent.trim();

              const isAvailable = variants.some(v => {
                const vOptVal = v.options[optIndex];
                if (vOptVal !== itemText && v['option' + pos] !== itemText) {
                  return false;
                }

                for (const [otherPos, otherVal] of Object.entries(currentSelections)) {
                  const otherPosNum = parseInt(otherPos, 10);
                  if (otherPosNum === pos) continue;
                  const otherIdx = otherPosNum - 1;
                  const vOtherVal = v.options[otherIdx];
                  if (vOtherVal !== otherVal && v['option' + otherPos] !== otherVal) {
                    return false;
                  }
                }

                return v.available === true;
              });

              if (isAvailable) {
                item.classList.remove('is-disabled');
              } else {
                item.classList.add('is-disabled');
              }
            });
          }
        });

        // Auto-reset invalid dropdown selection
        optionSelectors.forEach(selector => {
          const hiddenInput = selector.querySelector('input[type="hidden"][data-option-position]');
          if (!hiddenInput) return;
          const selectedText = hiddenInput.getAttribute('data-selected-text');
          if (!selectedText) return;

          const comboBox = selector.querySelector('combo-box');
          if (!comboBox) return;

          const selectedItem = Array.from(comboBox.querySelectorAll('.combo-box__option-item')).find(el => {
            const t = el.getAttribute('data-option-text') || el.textContent.trim();
            return t === selectedText;
          });

          if (selectedItem && selectedItem.classList.contains('is-disabled')) {
            hiddenInput.value = '';
            hiddenInput.removeAttribute('data-selected-text');
            const posNum = hiddenInput.getAttribute('data-option-position');
            const triggerBtn = selector.querySelector('.select--visible-enhanced');
            if (triggerBtn) {
              triggerBtn.classList.remove('has-selection');
              const valSpan = triggerBtn.querySelector('.select__selected-value');
              if (valSpan) {
                valSpan.classList.add('is-placeholder');
                const optName = selector.querySelector('.combo-box__title')?.textContent?.trim() || 'option';
                valSpan.textContent = 'Select ' + optName;
              }
            }
            const stickySelect = document.querySelector(`.product-sticky-form select[data-option-position="${posNum}"]`);
            if (stickySelect) {
              stickySelect.value = '';
              const stickyBtnEl = stickySelect.closest('.product-sticky-form__option-selector')?.querySelector('.select .select__selected-value');
              if (stickyBtnEl) {
                stickyBtnEl.classList.add('is-placeholder');
                stickyBtnEl.textContent = 'Select ' + (selector.querySelector('.combo-box__title')?.textContent?.trim() || 'option');
              }
            }
          }
        });
      }

      function updateFormState() {
        // First compute dynamic availability
        updateOptionsAvailability();

        const activeSelections = getActiveOptionSelections();
        const positions = Object.keys(activeSelections);
        if (!positions.length) return;

        let missingOptions = [];
        let selectedOptions = [];

        positions.forEach(pos => {
          const sel = activeSelections[pos];
          if (!sel.isSelected) {
            missingOptions.push(sel.name);
          } else {
            selectedOptions.push(sel);
          }
        });

        // If there are missing options
        if (missingOptions.length > 0) {
          if (atcButton) {
            atcButton.disabled = true;
            atcButton.classList.add('is-unselected', 'button--ternary');
            atcButton.classList.remove('button--primary', 'button--secondary');
            const nextMissing = missingOptions[0];
            const btnLabel = 'Select ' + nextMissing;
            atcButton.setAttribute('data-button-content', btnLabel);
            const textSpan = atcButton.querySelector('.product-form__add-button-text') || atcButton;
            if (textSpan) textSpan.textContent = btnLabel;
          }

          if (paymentButtonWrapper) {
            paymentButtonWrapper.style.display = 'none';
          }

          if (promptEl) {
            promptEl.classList.remove('hidden');
            const promptText = promptEl.querySelector('.prompt-text');
            if (promptText) {
              if (missingOptions.length === positions.length) {
                promptText.innerHTML = 'Please choose your options above to add to cart';
              } else {
                promptText.innerHTML = 'Almost there! Please select your <strong>' + missingOptions[0] + '</strong>';
              }
            }
          }

          if (stickyBtn) {
            stickyBtn.disabled = true;
            stickyBtn.classList.add('is-unselected', 'button--ternary');
            stickyBtn.classList.remove('button--primary', 'button--secondary');
            stickyBtn.textContent = 'Select ' + missingOptions[0];
          }

          const idInput = form.querySelector('input[name="id"]');
          if (idInput) {
            idInput.value = '';
            idInput.disabled = true;
          }
          return;
        }

        // All options are selected!
        if (promptEl) {
          promptEl.classList.add('hidden');
        }

        if (paymentButtonWrapper) {
          paymentButtonWrapper.style.display = '';
        }

        // Find matching variant from variants JSON
        if (variants.length > 0) {
          const matchedVariant = variants.find(v => {
            return selectedOptions.every(sel => {
              const optIndex = sel.position - 1;
              const vVal = v.options[optIndex];
              return vVal === sel.text || vVal === sel.value || (v['option' + sel.position] === sel.text) || (v['option' + sel.position] === sel.value);
            });
          });

          if (matchedVariant) {
            const idInput = form.querySelector('input[name="id"]');
            if (idInput) {
              idInput.value = matchedVariant.id;
              idInput.disabled = false;
            }

            if (atcButton) {
              if (matchedVariant.available) {
                atcButton.disabled = false;
                atcButton.classList.remove('is-unselected', 'button--ternary', 'button--secondary');
                atcButton.classList.add('button--primary');
                const defaultLabel = atcButton.getAttribute('data-original-label') || 'Add to Cart';
                atcButton.setAttribute('data-button-content', defaultLabel);
                const textSpan = atcButton.querySelector('.product-form__add-button-text') || atcButton;
                if (textSpan) textSpan.textContent = defaultLabel;
              } else {
                atcButton.disabled = true;
                atcButton.classList.remove('button--primary');
                atcButton.classList.add('is-unselected', 'button--ternary');
                atcButton.setAttribute('data-button-content', 'Sold Out');
                const textSpan = atcButton.querySelector('.product-form__add-button-text') || atcButton;
                if (textSpan) textSpan.textContent = 'Sold Out';
              }
            }

            if (stickyBtn) {
              if (matchedVariant.available) {
                stickyBtn.disabled = false;
                stickyBtn.classList.remove('is-unselected', 'button--ternary', 'button--secondary');
                stickyBtn.classList.add('button--primary');
                stickyBtn.textContent = 'Add to Cart';
              } else {
                stickyBtn.disabled = true;
                stickyBtn.classList.remove('button--primary');
                stickyBtn.classList.add('is-unselected', 'button--ternary');
                stickyBtn.textContent = 'Sold Out';
              }
            }
          }
        }
      }

      // Store original button label
      if (atcButton && !atcButton.getAttribute('data-original-label')) {
        const textSpan = atcButton.querySelector('.product-form__add-button-text') || atcButton;
        const initialText = textSpan.textContent.trim();
        if (initialText && !initialText.toLowerCase().includes('select')) {
          atcButton.setAttribute('data-original-label', initialText);
        } else {
          atcButton.setAttribute('data-original-label', 'Add to Cart');
        }
      }

      // If user hasn't interacted, enforce clean unselected state on load
      if (!window.__userInteractedWithVariants) {
        resetPickerToUnselected();
      }

      // Delegate click on combo-box options & cards
      picker.addEventListener('click', function(e) {
        const optionItem = e.target.closest('[role="option"], .combo-box__option-item');
        if (optionItem) {
          const comboBox = optionItem.closest('combo-box');
          if (!comboBox) return;

          const hiddenInput = comboBox.querySelector('input[data-option-position]');
          if (!hiddenInput) return;

          const optPos = parseInt(hiddenInput.getAttribute('data-option-position') || '1', 10);
          // Only block clicking disabled options for dependent options (position > 1)
          if (optPos > 1 && optionItem.classList.contains('is-disabled')) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          // User is interacting!
          window.__userInteractedWithVariants = true;

          const selectedText = optionItem.getAttribute('data-option-text') || optionItem.textContent.trim();
          const selectedVal = optionItem.getAttribute('value') || selectedText;

          hiddenInput.value = selectedVal;
          hiddenInput.setAttribute('data-selected-text', selectedText);

          // Update aria-selected in this combo-box
          comboBox.querySelectorAll('.combo-box__option-item').forEach(item => {
            item.setAttribute('aria-selected', item === optionItem ? 'true' : 'false');
          });

          // Update trigger button label & highlight
          const triggerBtn = comboBox.nextElementSibling?.matches('.select') ? comboBox.nextElementSibling : document.querySelector(`[aria-controls="${comboBox.id}"]`);
          if (triggerBtn) {
            const valueSpan = triggerBtn.querySelector('.select__selected-value');
            if (valueSpan) {
              valueSpan.textContent = selectedText;
              valueSpan.classList.remove('is-placeholder');
            }
            triggerBtn.classList.add('has-selection');
          }

          // Sync sticky form if present
          const stickySelect = document.querySelector(`.product-sticky-form select[data-option-position="${optPos}"]`);
          if (stickySelect) {
            stickySelect.value = selectedVal;
            const stickyBtnEl = stickySelect.closest('.product-sticky-form__option-selector')?.querySelector('.select .select__selected-value');
            if (stickyBtnEl) {
              stickyBtnEl.textContent = selectedText;
              stickyBtnEl.classList.remove('is-placeholder');
            }
          }

          // Trigger change event for Focal's VariantPicker
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

          // Update ATC button and validation states
          updateFormState();
          return;
        }

        // Handle clicks on amazon variant cards
        const card = e.target.closest('.amazon-variant-card');
        if (card) {
          const radio = card.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

      // Listen for radio change (amazon cards, block swatches)
      picker.addEventListener('change', function(e) {
        const radio = e.target.closest('input[type="radio"][data-option-position]');
        if (!radio) return;

        window.__userInteractedWithVariants = true;

        const card = radio.closest('.amazon-variant-card, .block-swatch');
        if (card) {
          card.parentElement?.querySelectorAll('.amazon-variant-card, .block-swatch').forEach(c => c.classList.remove('is-selected'));
          card.classList.add('is-selected');
        }

        const selector = radio.closest('.product-form__option-selector');
        if (selector) {
          const valSpan = selector.querySelector('.product-form__option-value');
          if (valSpan) {
            const text = radio.getAttribute('data-option-text') || radio.getAttribute('aria-label') || card?.querySelector('.amazon-variant-card__title')?.textContent?.trim() || radio.value;
            valSpan.textContent = text;
            valSpan.classList.remove('is-placeholder');
          }
        }

        updateFormState();
      });

      // Initial check on load
      updateFormState();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEnhancedVariantSelectors);
  } else {
    initEnhancedVariantSelectors();
  }

  // Support Focal's AJAX re-rendering
  document.addEventListener('product:rerender', function() {
    setTimeout(initEnhancedVariantSelectors, 50);
  });
})();